import React, { useState } from "react";
import { message, Modal } from "antd";
import ReactCrop, {
  centerCrop,
  convertToPixelCrop,
  makeAspectCrop,
  PercentCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { useTranslation } from "react-i18next";

export type CropShape = "rectangle" | "square" | null;

interface ResizableCropModalProps {
  open: boolean;
  imageUrl: string;
  onCancel: () => void;
  onOk: (croppedImageBlob: Blob) => void | Promise<void>;
  /**
   * "rectangle" → 16:9 aspect ratio, locked (move only).
   * "square" → 1:1 aspect ratio, locked (move only).
   * null/undefined → any aspect ratio, free crop (resize from all sides).
   */
  shape?: CropShape;
}

const ASPECT_RATIO = {
  rectangle: 16 / 9,
  square: 1,
} as const;

// Output compression: longest side capped, re-encoded as JPEG.
const MAX_OUTPUT_SIDE = 1600;
const JPEG_QUALITY = 0.85;
const CROP_AREA_HEIGHT = "min(55vh, 520px)";

const getCroppedImg = (
  image: HTMLImageElement,
  crop: PercentCrop,
): Promise<Blob> => {
  // Percent crop → natural (full-resolution) pixels, independent of on-screen size.
  const px = convertToPixelCrop(crop, image.naturalWidth, image.naturalHeight);
  const scale = Math.min(1, MAX_OUTPUT_SIDE / Math.max(px.width, px.height));

  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(px.width * scale));
  canvas.height = Math.max(1, Math.round(px.height * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) return Promise.reject(new Error("Canvas not supported"));

  // JPEG has no alpha: paint white so transparent PNGs don't turn black.
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    image,
    px.x,
    px.y,
    px.width,
    px.height,
    0,
    0,
    canvas.width,
    canvas.height,
  );

  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("Tainted canvas"))),
        "image/jpeg",
        JPEG_QUALITY,
      );
    } catch (e) {
      reject(e); // SecurityError on tainted (cross-origin) canvas
    }
  });
};

const ResizableCropModal: React.FC<ResizableCropModalProps> = ({
  open,
  imageUrl,
  onCancel,
  onOk,
  shape,
}) => {
  const fixedAspect = shape ? ASPECT_RATIO[shape] : undefined;
  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();
  const [imgEl, setImgEl] = useState<HTMLImageElement | null>(null);
  const [crop, setCrop] = useState<PercentCrop>();
  const [saving, setSaving] = useState(false);

  // Runs on every new image (modal body is destroyed on close), so no stale crop.
  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    setImgEl(e.currentTarget);
    setCrop(
      fixedAspect
        ? centerCrop(
            makeAspectCrop({ unit: "%", width: 100 }, fixedAspect, width, height),
            width,
            height,
          )
        : centerCrop({ unit: "%", width: 80, height: 80 }, width, height),
    );
  };

  const handleCrop = async () => {
    if (!imgEl || !crop?.width || !crop?.height || saving) return;

    let blob: Blob;
    try {
      blob = await getCroppedImg(imgEl, crop);
    } catch {
      messageApi.warning(
        t("message.warning.crop_edit_upload") ||
          "Cannot edit this image from the server. Re-upload the image to crop it.",
      );
      return;
    }

    setSaving(true);
    try {
      await onOk(blob);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {contextHolder}
      <Modal
        open={open}
        title={t("label.edit_image")}
        onCancel={onCancel}
        onOk={handleCrop}
        okText={t("button.done")}
        cancelText={t("button.cancel")}
        okButtonProps={{ disabled: !crop }}
        confirmLoading={saving}
        // Drags that end on the backdrop register as a mask click → modal closed mid-crop.
        maskClosable={false}
        keyboard={!saving}
        width="90%"
        style={{ maxWidth: "800px" }}
        styles={{ body: { padding: 16 } }}
        destroyOnHidden
      >
        <div
          style={{
            height: CROP_AREA_HEIGHT,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#f0f0f0",
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          {imageUrl && (
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              aspect={fixedAspect}
              locked={!!fixedAspect}
              keepSelection
              style={{ maxHeight: CROP_AREA_HEIGHT }}
            >
              {/* Natural aspect, no stretching: displayed box == image pixels. */}
              <img
                src={imageUrl}
                alt={t("label.edit_image")}
                onLoad={onImageLoad}
                style={{ display: "block", maxWidth: "100%", maxHeight: CROP_AREA_HEIGHT }}
              />
            </ReactCrop>
          )}
        </div>
        <div
          style={{
            marginTop: "16px",
            padding: "12px",
            backgroundColor: "#f5f5f5",
            borderRadius: "4px",
            textAlign: "center",
            color: "#666",
          }}
        >
          {fixedAspect
            ? t("label.crop_instructions_fixed") ||
              "Crop size is fixed. Drag the crop area to move it."
            : t("label.crop_instructions") ||
              "Drag the corners or edges to resize. Drag the center to move it."}
        </div>
      </Modal>
    </>
  );
};

export default ResizableCropModal;
